import { forwardRef, useState } from 'react';
import {
  Tooltip as MuiTooltip,
  Typography,
  TooltipProps as MuiTooltipProps,
  SxProps,
  ClickAwayListener,
} from '@mui/material';
import { tooltipStyle } from './Tooltip.styles';
import { useGetScreenSize } from '@utils/useGetScreenSize';

export const DEPRECATED_TOOLTIP_TEXT = 'This field is deprecated. It is still here to preserve existing data';

export interface TooltipProps extends MuiTooltipProps {
  tooltipBgColor?: string;
  tooltipTextColor?: string;
  config?: {
    allowWhiteSpace?: boolean;
    /** If true will to replace <b> tags with bold text, stripping other tags. */
    applyMarkup?: boolean;
  };
}

export const Tooltip = forwardRef<HTMLDivElement, TooltipProps>((props, ref) => {
  const { isMobileScreen } = useGetScreenSize();
  const [open, setOpen] = useState(false);

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = (e: any) => {
    e.stopPropagation();
    setOpen(true);
  };

  const {
    title,
    children,
    arrow = true,
    placement = 'top',
    tooltipTextColor = 'common.white',
    tooltipBgColor = 'primary.dark',
    sx = {},
    ...other
  } = props;

  const renderTooltipTitle = (raw: React.ReactNode) => {
    if (typeof raw !== 'string') return raw;

    let html = raw.replace(/<b>(.*?)<\/b>/g, '<b>$1</b>');
    // remove any other sort of html tags to prevent XSS, but allow <b> tags
    html = html.replace(/<(?!\/?b(?=>|\s.*>))\/?[^>]+>/g, '');

    return (
      <Typography
        variant='bodySmall'
        sx={{
          color: tooltipTextColor,
          ...(props.config?.allowWhiteSpace && { whiteSpace: 'pre-line' }),
        }}
        component='span'
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  };

  const tooltipContent = (
    <MuiTooltip
      {...other}
      ref={ref}
      title={title ? (props?.config?.applyMarkup ? renderTooltipTitle(title) : title) : undefined}
      placement={placement}
      disableFocusListener={isMobileScreen}
      disableHoverListener={isMobileScreen}
      disableTouchListener={isMobileScreen}
      arrow={arrow}
      onClose={isMobileScreen ? handleTooltipClose : undefined}
      open={isMobileScreen ? open : undefined}
      slotProps={{
        tooltip: {
          sx: {
            ...tooltipStyle,
            color: tooltipTextColor,
            backgroundColor: tooltipBgColor,
            '.MuiTooltip-arrow': {
              '&:before': {
                backgroundColor: tooltipBgColor,
              },
            },
            ...(sx as SxProps),
          },
        },
      }}
    >
      {children}
    </MuiTooltip>
  );

  return isMobileScreen ? (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <div role='button' tabIndex={0} onClick={handleTooltipOpen} onKeyDown={handleTooltipOpen}>
        {tooltipContent}
      </div>
    </ClickAwayListener>
  ) : (
    tooltipContent
  );
});

Tooltip.displayName = 'Tooltip';
